import { AnyEventObject, EventObject, Machine, MachineConfig, MachineOptions, assign } from "xstate"
import { SearchState } from "react-instantsearch-core"

export type AnswerSearchState = Pick<
	SearchState,
	"range" | "refinementList" | "toggle" | "sortBy" | "menu"
>

export interface Answer {
	label: string
	icon: () => JSX.Element
	nextQuestionId?: string
	description?: string
	searchState: AnswerSearchState
}

export interface Question {
	label: string
	description?: string
	answers: Answer[]
}

export interface ProductAdvisorContext {
	currentQuestionId: string
	selectedAnswers: {
		questionId: string
		answer: Answer
	}[]
}

export type ProductAdvisorMachineState = "askQuestion" | "validateAnswers" | "showResults"
export type ProductAdvisorMachineEvent =
	| "SELECT_ANSWER"
	| "SHOW_ANSWERS"
	| "SHOW_RESULTS"
	| "RESET"
	| "BACK"

const initialContext: ProductAdvisorContext = {
	currentQuestionId: "root",
	selectedAnswers: [],
}

// TODO: Use factory to create initial context/machine
const config: MachineConfig<ProductAdvisorContext, any, EventObject> = {
	id: "productAdvisor",
	initial: "askQuestion",
	context: initialContext,
	states: {
		askQuestion: {
			on: {
				SELECT_ANSWER: { target: "validateAnswers", actions: "selectAnswer" },
				SHOW_RESULTS: { target: "showResults", cond: "hasSelectedAnswers" },
				RESET: { target: "askQuestion", actions: "reset" },
				BACK: { target: "askQuestion", actions: "back", cond: "hasSelectedAnswers" },
			},
		},
		validateAnswers: {
			on: {
				SHOW_ANSWERS: "askQuestion",
				SHOW_RESULTS: "showResults",
			},
		},
		showResults: {
			type: "final",
			on: {
				RESET: { target: "askQuestion", actions: "reset" },
			},
		},
	},
}

const options: Partial<MachineOptions<ProductAdvisorContext, AnyEventObject>> = {
	actions: {
		selectAnswer: assign({
			currentQuestionId: (_, event) => {
				return event.answer?.nextQuestionId ?? null
			},
			selectedAnswers: (context, event) => {
				return [
					...context.selectedAnswers,
					{ questionId: context.currentQuestionId, answer: event.answer },
				]
			},
		}),
		reset: assign(initialContext),
		back: assign({
			currentQuestionId: (context) => {
				return context.selectedAnswers[context.selectedAnswers.length - 1].questionId
			},
			selectedAnswers: (context) => {
				const selectedAnswers = [...context.selectedAnswers]
				selectedAnswers.pop()

				return selectedAnswers
			},
		}),
	},
	guards: {
		hasSelectedAnswers: (context) => {
			return context.selectedAnswers.length > 0
		},
	},
}

const productAdvisorMachine = Machine<ProductAdvisorContext>(config, options)

export default productAdvisorMachine
